/* ============================================================
   Massage Warehouse — Custom Product Tabs
   Add to: Assets > mw-tabs.js
   Then include near </body> in theme.liquid with:
   {{ 'mw-tabs.js' | asset_url | script_tag }}

   Strategy: reads the raw product description from the Shopify
   product JSON already embedded on the page (data-product attr),
   so EasyTabs cannot intercept it. Replaces the EasyTabs container
   with our own widget. Safe to run alongside EasyTabs.
   ============================================================ */

(function () {
  'use strict';

  /* ----------------------------------------------------------
     CONFIGURATION
  ---------------------------------------------------------- */
  var CONFIG = {
    openTag: '[tab]',
    closeTag: '[/tab]',

    reviewsTabLabel: 'Reviews',
    reviewsSelector: '#judgeme_product_reviews',

    faqTabLabel: 'FAQ',
    faqSelector: '#disqus_thread',

    accordionBreakpoint: 768,
    defaultTab: 0,
  };

  /* ----------------------------------------------------------
     STEP 1: Get the raw description from the product JSON
     Shopify embeds it in data-product on the product form.
     This is untouched by EasyTabs.
  ---------------------------------------------------------- */
  function getRawDescription() {
    /* TurboTheme stores the product JSON as a string in data-product
       on the product form container div. Read it via dataset.product. */
    var el = document.querySelector('[data-product-form]');
    if (!el || !el.dataset.product) return null;
    try {
      var data = JSON.parse(el.dataset.product);
      return data.description || data.content || null;
    } catch (e) {
      return null;
    }
  }

  /* ----------------------------------------------------------
     STEP 2: Parse [tab]Name[/tab] markers
  ---------------------------------------------------------- */
  function parseTabsFromHTML(html) {
    /* Use plain string splitting — more reliable than regex with brackets.
       Split on [tab] first, then for each chunk extract label from [/tab]. */
    var open = CONFIG.openTag;   /* [tab]  */
    var close = CONFIG.closeTag; /* [/tab] */
    var tabs = [];

    /* Split the whole description on every [tab] occurrence */
    var parts = html.split(open);

    for (var i = 1; i < parts.length; i++) {
      /* Each part starts with "Label Name[/tab]...content..." */
      var closeIdx = parts[i].indexOf(close);
      if (closeIdx === -1) continue;

      var label = parts[i].substring(0, closeIdx).trim();
      var tabContent = parts[i].substring(closeIdx + close.length).trim();

      /* Decode HTML entities in label (e.g. &amp; -> &) */
      var tmp = document.createElement('textarea');
      tmp.innerHTML = label;
      label = tmp.value;

      if (label) {
        tabs.push({ label: label, html: tabContent });
      }
    }

    return tabs;
  }

  /* ----------------------------------------------------------
     STEP 3: Find ALL injection targets — TurboTheme renders
     the description twice (sidebar + full-width below fold)
  ---------------------------------------------------------- */
  function findAllInjectionTargets() {
    var targets = [];

    /* EasyTabs containers (may be multiple) */
    var ets = document.querySelectorAll('.easytabs-container');
    ets.forEach(function(et) { targets.push(et); });

    /* If no EasyTabs, find all .rte description blocks */
    if (targets.length === 0) {
      var rtes = document.querySelectorAll('.product-block--description .rte, .sixteen.columns.rte');
      rtes.forEach(function(rte) { targets.push(rte); });
    }

    return targets;
  }

  /* ----------------------------------------------------------
     STEP 4: Build the tab widget DOM
  ---------------------------------------------------------- */
  function buildTabWidget(tabs, staticTabs) {
    var container = document.createElement('div');
    container.className = 'mw-tabs-container';

    var bar = document.createElement('ul');
    bar.className = 'mw-tabs-bar';
    bar.setAttribute('role', 'tablist');

    var content = document.createElement('div');
    content.className = 'mw-tabs-content';

    var allTabs = tabs.concat(staticTabs);

    allTabs.forEach(function (tab, i) {
      var id = 'mw-tab-' + i + '-' + Date.now();

      /* Tab button */
      var li = document.createElement('li');
      li.setAttribute('role', 'tab');
      li.setAttribute('tabindex', i === CONFIG.defaultTab ? '0' : '-1');
      li.setAttribute('aria-controls', id);
      li.setAttribute('aria-selected', i === CONFIG.defaultTab ? 'true' : 'false');
      li.dataset.index = i;
      if (i === CONFIG.defaultTab) li.classList.add('mw-active');
      li.textContent = tab.label;

      /* Accordion header */
      var accHeader = document.createElement('div');
      accHeader.className = 'mw-accordion-header' + (i === CONFIG.defaultTab ? ' mw-active' : '');
      accHeader.setAttribute('role', 'tab');
      accHeader.setAttribute('tabindex', i === CONFIG.defaultTab ? '0' : '-1');
      accHeader.setAttribute('aria-controls', id);
      accHeader.dataset.index = i;
      accHeader.textContent = tab.label;
      var glyph = document.createElement('span');
      glyph.className = 'mw-accordion-glyph';
      accHeader.appendChild(glyph);

      /* Tab panel */
      var panel = document.createElement('div');
      panel.className = 'mw-tab-panel' + (i === CONFIG.defaultTab ? ' mw-active' : '');
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('id', id);
      panel.setAttribute('aria-hidden', i === CONFIG.defaultTab ? 'false' : 'true');

      if (tab.isStatic && tab.targetSelector) {
        var target = document.querySelector(tab.targetSelector);
        if (target) {
          panel.appendChild(target);
        } else {
          panel.innerHTML = '<p style="color:#999;font-style:italic;">Loading...</p>';
          (function (p, sel) {
            var attempts = 0;
            var poll = setInterval(function () {
              attempts++;
              var t = document.querySelector(sel);
              if (t && t.children.length > 0) {
                p.innerHTML = '';
                p.appendChild(t);
                clearInterval(poll);
              }
              if (attempts > 40) clearInterval(poll);
            }, 250);
          })(panel, tab.targetSelector);
        }
      } else {
        panel.innerHTML = tab.html;
      }

      var item = document.createElement('div');
      item.className = 'mw-tab-item';
      item.appendChild(accHeader);
      item.appendChild(panel);

      bar.appendChild(li);
      content.appendChild(item);
    });

    container.appendChild(bar);
    container.appendChild(content);

    return container;
  }

  /* ----------------------------------------------------------
     STEP 5: Events
  ---------------------------------------------------------- */
  function activate(widget, index) {
    var tabs = widget.querySelectorAll('.mw-tabs-bar li');
    var panels = widget.querySelectorAll('.mw-tab-panel');
    var accHeaders = widget.querySelectorAll('.mw-accordion-header');

    tabs.forEach(function (t, i) {
      var isActive = i === index;
      t.classList.toggle('mw-active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      t.setAttribute('tabindex', isActive ? '0' : '-1');
      t.classList.toggle('mw-before-active', i === index - 1);
    });

    panels.forEach(function (p, i) {
      var isActive = i === index;
      p.classList.toggle('mw-active', isActive);
      p.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    accHeaders.forEach(function (h, i) {
      h.classList.toggle('mw-active', i === index);
    });

  }

  function attachEvents(widget) {
    widget.querySelector('.mw-tabs-bar').addEventListener('click', function (e) {
      var li = e.target.closest('li[data-index]');
      if (!li) return;
      activate(widget, parseInt(li.dataset.index, 10));
    });

    widget.querySelector('.mw-tabs-content').addEventListener('click', function (e) {
      var h = e.target.closest('.mw-accordion-header[data-index]');
      if (!h) return;
      var idx = parseInt(h.dataset.index, 10);
      var isActive = h.classList.contains('mw-active');
      /* Record header position before activate() collapses other panels */
      var headerTop = h.getBoundingClientRect().top;
      activate(widget, isActive ? -1 : idx);
      /* After DOM update, scroll so header stays in same screen position */
      var newHeaderTop = h.getBoundingClientRect().top;
      var diff = newHeaderTop - headerTop;
      if (Math.abs(diff) > 2) {
        window.scrollBy(0, diff);
      }
    });

    widget.querySelector('.mw-tabs-bar').addEventListener('keydown', function (e) {
      var allLis = Array.from(widget.querySelectorAll('.mw-tabs-bar li'));
      var active = widget.querySelector('.mw-tabs-bar li.mw-active');
      var idx = allLis.indexOf(active);
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        var next = (idx + 1) % allLis.length;
        activate(widget, next);
        allLis[next].focus();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        var prev = (idx - 1 + allLis.length) % allLis.length;
        activate(widget, prev);
        allLis[prev].focus();
      }
    });
  }

  function checkAccordion(widget) {
    if (CONFIG.accordionBreakpoint <= 0) return;
    widget.classList.toggle('mw-accordion', window.innerWidth < CONFIG.accordionBreakpoint);
  }

  /* ----------------------------------------------------------
     INIT
  ---------------------------------------------------------- */
  function init() {
    /* Get raw description from product JSON — bypasses EasyTabs */
    var rawHTML = getRawDescription();
    if (!rawHTML) return;

    /* Only run on products that use our [tab] markers */
    if (rawHTML.indexOf(CONFIG.openTag) === -1) return;

    var tabs = parseTabsFromHTML(rawHTML);
    if (tabs.length === 0) return;

    /* Find all injection targets (TurboTheme renders description twice) */
    var targets = findAllInjectionTargets();
    if (targets.length === 0) return;

    /* Build static tabs */
    var staticTabs = [];
    if (CONFIG.reviewsTabLabel && CONFIG.reviewsSelector) {
      staticTabs.push({
        label: CONFIG.reviewsTabLabel,
        isStatic: true,
        targetSelector: CONFIG.reviewsSelector
      });
    }
    if (CONFIG.faqTabLabel && CONFIG.faqSelector) {
      staticTabs.push({
        label: CONFIG.faqTabLabel,
        isStatic: true,
        targetSelector: CONFIG.faqSelector
      });
    }

    /* Replace every instance — first one gets full widget,
       subsequent ones (TurboTheme full-width duplicate) get hidden */
    targets.forEach(function(target, idx) {
      if (idx === 0) {
        var widget = buildTabWidget(tabs, staticTabs);
        attachEvents(widget);
        target.parentNode.replaceChild(widget, target);
        checkAccordion(widget);

        /* Stop TurboTheme flex layout stretching rte beyond widget height */
        var rte = widget.closest('.sixteen.columns.rte');
        if (rte) rte.style.alignSelf = 'flex-start';

        window.addEventListener('resize', function () { checkAccordion(widget); });

      } else {
        /* Hide the duplicate container so it leaves no whitespace */
        target.style.display = 'none';
      }
    });
  }

  function removeCbbMargin() {
    var cbb = document.querySelector('.cbb-also-bought-container');
    if (cbb) {
      cbb.style.setProperty('margin-top', '0', 'important');
      /* Also watch for re-renders by the app */
      var observer = new MutationObserver(function() {
        var c = document.querySelector('.cbb-also-bought-container');
        if (c) c.style.setProperty('margin-top', '0', 'important');
      });
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    } else {
      setTimeout(removeCbbMargin, 1000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { init(); removeCbbMargin(); });
  } else {
    init();
    removeCbbMargin();
  }

})();